import { useState } from "react";
import { Check, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ImportarEmpleadosAyudaInline, DescargarPlantillaEmpleadosCsv } from "@/components/ImportarEmpleadosAyuda";

function itemDesdePreview(n) {
  return {
    email: n.email,
    password: n.password,
    nombre: n.nombre,
    apellidos: n.apellidos,
    rol: n.rol,
    departamento_id: n.departamento_id ?? null,
    puesto_id: n.puesto_id ?? null,
    departamento: n.departamento || null,
    puesto: n.puesto || null,
    telefono: n.telefono || "",
    dni_nie: n.dni_nie || "",
    direccion: n.direccion || "",
    poblacion: n.poblacion || "",
    provincia: n.provincia || "",
    tipo_contrato_id: n.tipo_contrato_id ?? null,
    tipo_contrato: n.tipo_contrato || null,
    dias_vacaciones: n.dias_vacaciones,
    fecha_inicio_contrato: n.fecha_inicio_contrato,
    fecha_nacimiento: n.fecha_nacimiento,
    fecha_baja: n.fecha_baja,
    situacion: n.situacion,
    vacaciones_modo: n.vacaciones_modo,
    dias_asuntos_propios: n.dias_asuntos_propios,
    vacaciones_usados: n.vacaciones_usados,
    asuntos_usados: n.asuntos_usados,
    pin: n.pin,
    nfc_uid: n.nfc_uid,
    comm_email: n.comm_email,
    comm_whatsapp: n.comm_whatsapp,
    comm_sms: n.comm_sms,
    comm_telegram: n.comm_telegram,
    cumpleanos_avisar_companeros: n.cumpleanos_avisar_companeros,
    tarifa_coste_hora: n.tarifa_coste_hora ?? null,
    tarifa_venta_hora: n.tarifa_venta_hora ?? null,
  };
}

export default function ImportarEmpleadosDialog({ open, onOpenChange, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [selCrear, setSelCrear] = useState({});
  const [selCampos, setSelCampos] = useState({});
  const [selPassword, setSelPassword] = useState({});

  const reset = () => {
    setPreview(null);
    setSelCrear({});
    setSelCampos({});
    setSelPassword({});
  };

  const analizar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("archivo", file);
    try {
      const { data } = await api.post("/usuarios/importar/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const crear = {};
      const campos = {};
      const pwd = {};
      (data.nuevos || []).forEach((n) => { crear[n.email] = true; });
      (data.actualizaciones || []).forEach((a) => {
        campos[a.email] = {};
        a.cambios.forEach((c) => {
          campos[a.email][c.campo] = c.campo !== "password";
        });
        pwd[a.email] = a.cambios.some((c) => c.campo === "password");
      });
      setSelCrear(crear);
      setSelCampos(campos);
      setSelPassword(pwd);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const confirmar = async () => {
    if (!preview) return;
    setConfirmando(true);
    try {
      const crear = (preview.nuevos || [])
        .filter((n) => selCrear[n.email])
        .map((n) => itemDesdePreview(n));
      const actualizar = (preview.actualizaciones || [])
        .map((a) => {
          const campos = Object.entries(selCampos[a.email] || {})
            .filter(([, v]) => v)
            .map(([k]) => k)
            .filter((k) => k !== "password");
          const actualizar_password = selPassword[a.email] && a.password;
          if (!campos.length && !actualizar_password) return null;
          return {
            ...itemDesdePreview({ ...a.propuesto, email: a.email, password: a.password }),
            campos,
            actualizar_password: !!actualizar_password,
            password: a.password,
          };
        })
        .filter(Boolean);

      const { data } = await api.post("/usuarios/importar/confirmar", { crear, actualizar });
      toast.success(
        `${data.resumen.creados} alta(s) inactiva(s) · ${data.resumen.actualizados} actualización(es)`
      );
      onSuccess?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="importar-empleados-dialog">
        <DialogHeader>
          <DialogTitle>Importar empleados desde CSV</DialogTitle>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-600">
              Los <strong>nuevos</strong> se darán de alta como <strong>inactivos</strong> (revísalos y actívalos cuando
              estén listos). Si el email ya existe, se compararán los datos y podrás confirmar cada cambio.
            </p>
            <ImportarEmpleadosAyudaInline mostrarBotonPlantilla={false} />
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
              <DescargarPlantillaEmpleadosCsv className="border-violet-200 text-violet-900 hover:bg-violet-50" />
              <input type="file" accept=".csv,.xlsx,.xlsm" className="hidden" id="csv-import-dialog" onChange={analizar} />
              <Button asChild disabled={loading} className="gap-2">
                <label htmlFor="csv-import-dialog" className="cursor-pointer">
                  <Upload className="h-4 w-4" />
                  {loading ? "Analizando…" : "Seleccionar CSV o Excel"}
                </label>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-emerald-600">{preview.resumen.nuevos} nuevos</Badge>
              <Badge className="bg-amber-500">{preview.resumen.actualizaciones} con cambios</Badge>
              <Badge variant="outline">{preview.resumen.sin_cambios} sin cambios</Badge>
              {preview.resumen.errores > 0 && <Badge variant="destructive">{preview.resumen.errores} errores</Badge>}
            </div>

            {preview.organizacion_creada &&
              [
                ...(preview.organizacion_creada.centros || []),
                ...(preview.organizacion_creada.departamentos || []),
                ...(preview.organizacion_creada.puestos || []),
                ...(preview.organizacion_creada.tipos_contrato || []),
              ].length > 0 && (
                <div className="rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-2 text-xs text-sky-900">
                  <p className="font-semibold">Organización creada automáticamente</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5">
                    {(preview.organizacion_creada.centros || []).map((n) => (
                      <li key={`c-${n}`}>Centro: {n}</li>
                    ))}
                    {(preview.organizacion_creada.departamentos || []).map((n) => (
                      <li key={`d-${n}`}>Departamento: {n}</li>
                    ))}
                    {(preview.organizacion_creada.puestos || []).map((n) => (
                      <li key={`p-${n}`}>Puesto: {n}</li>
                    ))}
                    {(preview.organizacion_creada.tipos_contrato || []).map((n) => (
                      <li key={`t-${n}`}>Tipo de contrato: {n}</li>
                    ))}
                  </ul>
                </div>
              )}

            {preview.nuevos?.length > 0 && (
              <div>
                <p className="mb-2 font-semibold text-zinc-800">Altas nuevas (inactivas)</p>
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-emerald-100 bg-emerald-50/50 p-2">
                  {preview.nuevos.map((n) => (
                    <li key={n.email} className="flex items-start gap-2">
                      <Checkbox checked={!!selCrear[n.email]} onCheckedChange={(v) => setSelCrear({ ...selCrear, [n.email]: v === true })} className="mt-0.5" />
                      <span>
                        {n.nombre} {n.apellidos} · <span className="text-zinc-500">{n.email}</span>
                        {(n.dni_nie || n.departamento || n.puesto) && (
                          <span className="mt-0.5 block text-xs text-zinc-500">
                            {[n.dni_nie && `DNI ${n.dni_nie}`, n.departamento, n.puesto].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.actualizaciones?.length > 0 && (
              <div>
                <p className="mb-2 font-semibold text-zinc-800">Actualizaciones pendientes de confirmar</p>
                <div className="max-h-64 space-y-3 overflow-y-auto">
                  {preview.actualizaciones.map((a) => (
                    <div key={a.email} className="rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                      <p className="font-medium">{a.nombre} <span className="text-zinc-500">({a.email})</span></p>
                      <ul className="mt-2 space-y-1">
                        {a.cambios.map((c) => (
                          <li key={c.campo} className="flex items-start gap-2 text-xs">
                            <Checkbox
                              checked={c.campo === "password" ? !!selPassword[a.email] : !!selCampos[a.email]?.[c.campo]}
                              onCheckedChange={(v) => {
                                if (c.campo === "password") {
                                  setSelPassword({ ...selPassword, [a.email]: v === true });
                                } else {
                                  setSelCampos({
                                    ...selCampos,
                                    [a.email]: { ...selCampos[a.email], [c.campo]: v === true },
                                  });
                                }
                              }}
                            />
                            <span>
                              <strong>{c.label}:</strong> {c.antes} → {c.despues}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.errores?.length > 0 && (
              <div className="rounded-lg border border-red-100 bg-red-50 p-2 text-xs text-red-800">
                {preview.errores.map((e, i) => (
                  <p key={i}>Fila {e.fila}: {e.email || "—"} — {e.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {preview && (
            <>
              <Button variant="outline" onClick={reset} className="gap-1"><X className="h-4 w-4" /> Otro archivo</Button>
              <Button onClick={confirmar} disabled={confirmando} className="gap-1 bg-violet-600 hover:bg-violet-700">
                <Check className="h-4 w-4" />
                {confirmando ? "Aplicando…" : "Confirmar importación"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
